import React, { ReactNode } from 'react';
import { useEffect, useState } from "react";
import { Fill, Stroke, Style } from "ol/style";
import Chart from "ol-ext/style/Chart";
import './App.css';

import { GeoMap } from "./GeoMap";
import { Selector } from './Selector';

import { kmeans } from 'ml-kmeans';
import { BORDER_GREY, LegendConfig, LegendRow, NEUTRAL_STYLE, numberWithCommas } from './GeoMap/legend';

const MAX_COLOR = [44, 123, 182];
const LMID_COLOR = [253, 174, 97];
const MMID_COLOR = [255, 255, 191];
const UMID_COLOR = [171, 217, 233];
const MIN_COLOR = [215, 25, 28];
const COLORZ0 = [MIN_COLOR, LMID_COLOR, MMID_COLOR, UMID_COLOR, MAX_COLOR];
const COLORZ = [MIN_COLOR, LMID_COLOR, MAX_COLOR];
const KMEANS_CLUSTER_SIZE = 4;
const PIE_SIZES = [10, 30];
const PIE_COLORZ = ["#001219", "#005f73", "#0a9396", "#94d2bd", "#e9d8a6", "#ee9b00", "#ca6702", "#bb3e03", "#ae2012", "#9b2226"];


const interpolateColor = (colors: number[][], limits: number[], value: number): number[] => {
    const range = limits[1] - limits[0];
    if (range === 0)
        return colors[1];
    const diff = 1.0 * (value - limits[0]);
    return colors[0].map((val, idx) => val + diff * (colors[1][idx] - val) / range);
}

const formatDisplayValue = (valoare:number): string|number =>{
    if (valoare === null || valoare === undefined)
        return "valoare confidențială";
    else
        return numberWithCommas(valoare);
}




export const App: React.FC = () => {

    const [styleFunk, setStyleFunk] = useState<((propertyValue: string | number) => Style | Style[] | undefined) | undefined>(undefined);
    const [centroidStyleFunk, setCentroidStyleFunk] = useState<((propertyValue: string | number) => Style | Style[] | undefined) | undefined>(undefined);
    const [data, setData] = useState<{ [key: number]: number } | undefined>(undefined);
    const [info, setInfo] = useState<ReactNode | undefined>(undefined);
    const [geomConfig, setGeomConfig] = useState<{ polygons: string, centroids: string | undefined, poly_code_col: string, centroid_code_col: string } | undefined>(undefined);
    const [legendConfig, setLegendConfig] = useState<LegendConfig | undefined>(undefined);
    const [useProcent, setUseProcent] = useState<boolean>(false);
    const [usePie, setUsePie] = useState<boolean>(false);

    useEffect(() => {
        setInfo(undefined);
    }, [data])

    const updateInfo = (featProps: any) => {
        if (featProps && data && geomConfig?.poly_code_col) {
            let valoare: any = data[+featProps[geomConfig.poly_code_col]];
            console.log(valoare);
            setInfo(<div>
                <h3>{featProps['name']}: {formatDisplayValue(usePie ? valoare.total : valoare)}{useProcent ? '%' : ''}</h3>
                {usePie ? (<table>
                    {valoare.slices.map((v: any[]) => (<tr><td>{v[0]}</td><td>{formatDisplayValue(v[1])}</td></tr>))}
                </table>) : ''}
                </div>);
        }
        else
            setInfo(undefined);
    }

    const updateGeom = (geomLayer: string | undefined) => {
        switch (geomLayer) {
            case 'JUDET':
                setGeomConfig({
                    polygons: "geospatial:ro_judete_poligon",
                    poly_code_col: "county_code",
                    centroids: "recensamant:judete_centroid",
                    centroid_code_col: "code"
                })
                break;
            case 'UAT':
                setGeomConfig({
                    polygons: "geospatial:ro_uat_poligon",
                    poly_code_col: "natcode",
                    centroids: undefined,
                    centroid_code_col: "code"
                })
                break;
            case 'LOC':
                setGeomConfig({
                    polygons: "geospatial:ro_localitati_punct",
                    poly_code_col: "natCode",
                    centroids: undefined,
                    centroid_code_col: "code"
                })
                break;
            default:
                setGeomConfig(undefined);
        }
    }


    const updateData = (payload: { [key: number]: any } | undefined) => {
        //console.log(payload);
        setData(payload);
        if (!payload) {
            setStyleFunk(undefined);
            return;
        }
        const ovaluz = usePie ?
            Object.values(payload).map((val) => val.total).filter((val) => (val !== null && val !== undefined)).sort((a, b) => a - b) :
            Object.values(payload).filter((val) => (val !== null && val !== undefined)).sort((a, b) => a - b);
        //console.log(ovaluz);
        const valuz = Array.from(new Set(ovaluz)).sort((a, b) => a - b);
        var clusterz: number[] = [];
        var coleurs: number[][] = COLORZ;
        if ((geomConfig?.polygons.indexOf('uat') || -1) > 0 && valuz.length >= 4) {

            const initial_clusters: number[][] = [];
            for (var i = 0; i < valuz.length; i = i + Math.ceil(valuz.length / KMEANS_CLUSTER_SIZE))
                initial_clusters.push([valuz[i]]);
            while (initial_clusters.length >= KMEANS_CLUSTER_SIZE)
                initial_clusters.pop();
            if (initial_clusters.length < KMEANS_CLUSTER_SIZE)
                initial_clusters.push([valuz[valuz.length - 1]]);
            //console.log(KMEANS_CLUSTER_SIZE, "clusterz", initial_clusters);
            const ans = kmeans(valuz.map((v) => [v]), KMEANS_CLUSTER_SIZE, { initialization: initial_clusters });
            for (var j = 0; j < ans.clusters.length; j++) {
                if (clusterz.length === 0)
                    clusterz.push(j);
                else if (ans.clusters[j] !== ans.clusters[j - 1])
                    clusterz.push(j - 1);
                if (ans.clusters[j] === KMEANS_CLUSTER_SIZE - 1)
                    break;
            };
            clusterz.push(valuz.length - 1);
            coleurs = COLORZ0;
        }
        else if (valuz.length > 2) {
            const max = valuz[valuz.length - 1];
            if (max > valuz[valuz.length - 2] * 1.5) {
                const midPoint = 0.5 * (valuz[0] + valuz[valuz.length - 2]);
                //console.log("midpoint", midPoint);
                //console.log(valuz, valuz.findIndex((val) => val > midPoint));
                clusterz = [0, valuz.findIndex((val) => val > midPoint) as number, valuz.length - 1];
            }
            else {
                const midPoint = 0.5 * (valuz[0] + max);
                clusterz = [0, valuz.findIndex((val) => val > midPoint) as number, valuz.length - 1];
            }
        }
        else {
            clusterz = [0, valuz.length - 1];
            coleurs = [MIN_COLOR, MAX_COLOR];
        }
        //console.log("set kegend config", coleurs, clusterz.map((val) => valuz[val]));
        const pieLimits: number[] = [valuz[0], valuz[valuz.length - 1]];
        const pieRows: LegendRow[] = [];
        if (usePie) {
            var pidx = 0;
            var oneObject = Object.values(payload)[0];
            if (oneObject) {
                //console.log(oneObject);
                for (var slice of oneObject.slices) {
                    console.log(slice, pidx, PIE_COLORZ[pidx % PIE_COLORZ.length]);
                    pieRows.push({
                        title: slice[0], typeGeom: 'Polygon',
                        style: new Style({
                            fill: new Fill({ color: PIE_COLORZ[pidx % PIE_COLORZ.length] })
                        })
                    })
                    pidx++;
                }
            }
        }

        setLegendConfig({
            colors: coleurs, thresholds: clusterz.map((val) => valuz[val]),
            hasPie: usePie, pieLimits: pieLimits, pieSizes: PIE_SIZES, pieRows: pieRows
        });

        const CACHE: { [key: string | number]: Style | Style[] } = {};

        const newStyleFunk = (propertyValue: string | number) => {
            //debugger;
            const propNo: number = +propertyValue;
            if (propNo in payload) {
                const val = usePie ? payload[propNo].total : payload[propNo];
                let style = undefined;
                if (val in CACHE)
                    return CACHE[val];
                if (val === null || val === undefined)
                    return NEUTRAL_STYLE;
                else {
                    //debugger;
                    if (valuz.length === 1)
                        style = new Style({
                            fill: new Fill({
                                color: MAX_COLOR
                            }),
                            stroke: new Stroke({
                                color: BORDER_GREY,
                                width: 1
                            })
                        });
                    else {
                        const idx = valuz.indexOf(val);
                        let cIdx = clusterz.length - 1;
                        for (var i = 0; i < clusterz.length; i++) {
                            if (idx < clusterz[i]) {
                                cIdx = i;
                                break;
                            }
                        }
                        const colorz = [coleurs[cIdx - 1], coleurs[cIdx]];
                        const limits = [valuz[clusterz[cIdx - 1]], valuz[clusterz[cIdx]]];
                        //console.log(`interpolez ${val} (${idx}) intre ${cIdx - 1} si ${cIdx}`, colorz, coleurs);
                        style = new Style({
                            fill: new Fill({
                                color: interpolateColor(colorz, limits, val)
                            }),
                            stroke: new Stroke({
                                color: BORDER_GREY,
                                width: 1
                            })
                        });
                    }

                }
                CACHE[val] = style;
                return style;

            }
        }
        setStyleFunk(() => newStyleFunk);

        if (usePie) {
            const PIE_CACHE: { [key: string | number]: Style | Style[] } = {};            
            
            const newCentroidStyleFunk = (propertyValue: string | number) => {
                //debugger;
                const propNo: number = +propertyValue;
                if (propNo in payload) {
                    
                    const val = payload[propNo].total;
                    let style = undefined;
                    if (val in PIE_CACHE)
                        return PIE_CACHE[val];
                    if (val === null || val === undefined)
                        return undefined;
                    else {
                        style = new Style({
                            image: new Chart({
                                type: "pie",
                                colors: PIE_COLORZ,
                                radius: 1. * (payload[propNo].total - pieLimits[0]) * (PIE_SIZES[1] - PIE_SIZES[0]) / (pieLimits[1] - pieLimits[0]) + PIE_SIZES[0],
                                data: payload[propNo].slices.map((v:any[])=>v[1]),
                                stroke: new Stroke({
                                    color: "#fff",
                                    width: 1
                                })
                            })
                        });

                    }
                    PIE_CACHE[val] = style;
                    return style;
                }
            }
            setCentroidStyleFunk(() => newCentroidStyleFunk);
        }
        else
            setCentroidStyleFunk(undefined);
    }

    return (
        <div className="App">
            <div className="SelectorParent">
                <Selector
                    onSelect={updateData}
                    useProcent={useProcent}
                    onProcentUpdate={() => setUseProcent(!useProcent)}
                    usePie={usePie}
                    onPieUpdate={() => setUsePie(!usePie)}
                    onGeomChange={updateGeom}
                />
                <div className="InfoDiv">
                    {info}
                </div>
            </div>
            <GeoMap
                mapDivId="geomap-1"
                legendConfig={legendConfig}
                geomConfig={geomConfig}
                styleFunction={styleFunk}
                centroidStyleFunction={centroidStyleFunk}
                onSelect={updateInfo}
            />
        </div>
    );
}

export default App;
